import React, { useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Button,
  Card,
  Input,
  Segmented,
  Select,
  Tag,
  Typography,
} from "antd";
import { skipToken } from "@reduxjs/toolkit/query";
import PageHeader from "../../../components/PageHeader";
import ActivityIndicator from "../../../components/ActivityIndicator";
import {
  useGetSchoolTeachersProgressQuery,
  useGetTeacherProgressQuery,
  useLazySearchTeachersQuery,
} from "../../../store/api/teacherProgressApi";
import { useGetSchoolsQuery } from "../../../store/api/schoolsApi";
import { TeacherSummary } from "../../../services/teacherProgress/teacherProgress.types";
import { resolveRoleType } from "../../../components/Forms/UserForm/utils";
import {
  MIN_SEARCH_LENGTH,
  buildSchoolOptions,
  formatTeacherName,
  teacherInitials,
} from "../../../utils/teacherProgress/teacher-progress-display";
import ProgramsCard from "./ProgramsCard";
import SchoolProgressCard from "./SchoolProgressCard";
import styles from "./styles.module.scss";

const SEARCH_LIMIT = 20;

type SearchMode = "teacher" | "school";

const MODE_OPTIONS: { value: SearchMode; label: string }[] = [
  { value: "teacher", label: "Por profesor" },
  { value: "school", label: "Por escuela" },
];

const TeacherCard: React.FC<{ teacher: TeacherSummary }> = ({ teacher }) => (
  <Card>
    <div className={styles.teacher}>
      <Avatar size={56} style={{ backgroundColor: "var(--bo-primary)" }}>
        {teacherInitials(teacher)}
      </Avatar>
      <div className={styles.teacherInfo}>
        <span className={styles.teacherName}>{formatTeacherName(teacher)}</span>
        <span className={styles.teacherMeta}>
          {teacher.email ?? "Sin correo"}
        </span>
        <span className={styles.teacherMeta}>
          {teacher.school?.name ?? "Sin escuela"}
        </span>
      </div>
      <Tag color="purple">{resolveRoleType(teacher.role)}</Tag>
    </div>
  </Card>
);

const TeacherProgressPage: React.FC = () => {
  const [query, setQuery] = useState("");
  const [tooShort, setTooShort] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [searchTeachers, searchResult] = useLazySearchTeachersQuery();
  const detail = useGetTeacherProgressQuery(selectedId ?? skipToken);
  const [mode, setMode] = useState<SearchMode>("teacher");
  const [schoolId, setSchoolId] = useState<number | null>(null);
  const schools = useGetSchoolsQuery(undefined, { skip: mode !== "school" });
  const schoolProgress = useGetSchoolTeachersProgressQuery(
    mode === "school" && schoolId !== null ? schoolId : skipToken,
  );
  const schoolOptions = useMemo(
    () => buildSchoolOptions(schools.data ?? []),
    [schools.data],
  );

  const openTeacher = (teacherId: number) => {
    setMode("teacher");
    setSelectedId(teacherId);
  };

  const handleSearch = async () => {
    const q = query.trim();
    if (q.length < MIN_SEARCH_LENGTH) {
      setTooShort(true);
      return;
    }
    setTooShort(false);
    setSelectedId(null);
    try {
      const teachers = await searchTeachers({
        q,
        limit: SEARCH_LIMIT,
      }).unwrap();
      if (teachers.length === 1) setSelectedId(teachers[0].id);
    } catch {
      setSelectedId(null);
    }
  };

  const teachers = searchResult.isSuccess ? searchResult.data : undefined;
  const showResults = !tooShort && !searchResult.isFetching;

  return (
    <div className={styles.page}>
      <div>
        <PageHeader title="Progreso de profesores" />
        <Typography.Paragraph className={styles.subtitle}>
          Consulta la última semana que cada profesor vio en sus programas.
        </Typography.Paragraph>
      </div>

      <Card
        title={mode === "teacher" ? "Buscar profesor" : "Buscar escuela"}
        extra={
          <Segmented
            value={mode}
            onChange={(value) => setMode(value as SearchMode)}
            options={MODE_OPTIONS}
          />
        }
      >
        {mode === "school" && (
          <>
            <Select
              aria-label="Escuela"
              className={styles.schoolSelect}
              showSearch
              optionFilterProp="label"
              placeholder="Selecciona una escuela"
              loading={schools.isFetching}
              value={schoolId ?? undefined}
              onChange={setSchoolId}
              options={schoolOptions}
            />
            {schools.isError && (
              <Alert
                type="error"
                showIcon
                style={{ marginTop: 16 }}
                message="No se pudieron cargar las escuelas."
                action={
                  <Button size="small" onClick={() => schools.refetch()}>
                    Reintentar
                  </Button>
                }
              />
            )}
          </>
        )}
        {mode === "teacher" && (
          <>
            <div className={styles.searchRow}>
              <Input
                placeholder="Nombre o correo del profesor"
                value={query}
                maxLength={256}
                onChange={(event) => setQuery(event.target.value)}
                onPressEnter={handleSearch}
              />
              <Button
                type="primary"
                onClick={handleSearch}
                loading={searchResult.isFetching}
              >
                Buscar
              </Button>
            </div>
            {tooShort && (
              <Typography.Text type="danger" className={styles.hint}>
                Escribe al menos 2 caracteres para buscar.
              </Typography.Text>
            )}
            {showResults && searchResult.isError && (
              <Alert
                type="error"
                showIcon
                style={{ marginTop: 16 }}
                message="No se pudo buscar profesores."
              />
            )}
            {showResults && teachers?.length === 0 && (
              <Typography.Text type="secondary" className={styles.hint}>
                No encontramos profesores con esa búsqueda.
              </Typography.Text>
            )}
            {showResults && teachers && teachers.length > 1 && (
              <ul className={styles.results}>
                {teachers.map((teacher) => (
                  <li key={teacher.id}>
                    <Button
                      type={teacher.id === selectedId ? "default" : "text"}
                      className={styles.resultButton}
                      onClick={() => setSelectedId(teacher.id)}
                    >
                      <span>{formatTeacherName(teacher)}</span>
                      <span className={styles.resultMeta}>
                        {[teacher.email, teacher.school?.name]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </Card>

      {mode === "school" && schoolId !== null && schoolProgress.isFetching && (
        <ActivityIndicator />
      )}
      {mode === "school" &&
        schoolId !== null &&
        schoolProgress.isError &&
        !schoolProgress.isFetching && (
          <Alert
            type="error"
            showIcon
            message="No se pudo cargar el progreso de la escuela."
            action={
              <Button size="small" onClick={() => schoolProgress.refetch()}>
                Reintentar
              </Button>
            }
          />
        )}
      {mode === "school" && schoolProgress.currentData && (
        <SchoolProgressCard
          key={schoolProgress.currentData.school.id}
          progress={schoolProgress.currentData}
          onOpenTeacher={openTeacher}
        />
      )}

      {mode === "teacher" && selectedId !== null && detail.isLoading && (
        <ActivityIndicator />
      )}
      {mode === "teacher" && selectedId !== null && detail.isError && (
        <Alert
          type="error"
          showIcon
          message="No se pudo cargar el progreso del profesor."
          action={
            <Button size="small" onClick={() => detail.refetch()}>
              Reintentar
            </Button>
          }
        />
      )}
      {mode === "teacher" &&
        selectedId !== null &&
        detail.data &&
        detail.data.teacher.id === selectedId && (
          <>
            <TeacherCard teacher={detail.data.teacher} />
            <ProgramsCard
              key={detail.data.teacher.id}
              programs={detail.data.programs}
            />
          </>
        )}
    </div>
  );
};

export default TeacherProgressPage;
